import { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Leaf } from 'lucide-react';
import gsap from 'gsap';
// import { useGSAP } from '../../hooks/use-gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function HeroSection() {
  const containerRef = useRef(null);
  const lensRef = useRef(null);
  const contentRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);

    if (!containerRef.current || !lensRef.current || !contentRef.current)
      return;

    // Clear existing ScrollTriggers
    ScrollTrigger.getAll().forEach(t => t.kill());

    // Initial setup - position lens at the top
    gsap.set(lensRef.current, {
      scale: 1,
      opacity: 1,
      yPercent: -50, // Start above the viewport
      transformOrigin: 'center center'
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top top',
        end: '+=100%', // Reduced from 150% to 100%
        scrub: 0.5, // Reduced from 1 to 0.5 for faster response
        pin: true,
        pinSpacing: true
      }
    });

    // Animation sequence
    tl.to(lensRef.current, {
      yPercent: 0, // Move to center
      duration: 0.3, // Reduced from 1 to 0.3
      ease: 'power2.inOut'
    })
      .to(lensRef.current, {
        scale: 8, // Reduced from 12 to 8
        opacity: 0, // Added opacity fade out
        duration: 0.4, // Reduced from 1 to 0.4
        ease: 'power2.in'
      })
      .to(
        contentRef.current,
        {
          opacity: 0,
          y: -30, // Reduced from -50 to -30
          duration: 0.3, // Reduced from 1 to 0.3
          ease: 'power2.in'
        },
        '<'
      );

    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [isLoaded]);

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen overflow-hidden will-change-transform"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50/50 to-blue-50/30">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.1) 1px, transparent 0)`,
              backgroundSize: '32px 32px'
            }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div
        ref={contentRef}
        className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4"
      >
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center justify-center p-3 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl shadow-lg mb-6"
          >
            <Leaf className="w-8 h-8 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-gray-900 mb-8"
          >
            Discover & Learn About
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
              Every Plant You See
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-600"
          >
            Take a photo or upload an image of any plant, and our AI will help
            you identify and learn everything about it in seconds.
          </motion.p>
        </div>

        {/* Lens Effect */}
        <div
          ref={lensRef}
          className="fixed top-0 left-1/2 -translate-x-1/2 w-72 h-72 md:w-96 md:h-96 will-change-transform"
          style={{ perspective: 1000 }}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="relative w-full h-full rounded-full bg-gradient-to-br from-white/90 to-white/50 backdrop-blur-lg shadow-[0_0_100px_rgba(16,185,129,0.3)] transform-gpu"
            style={{
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
          >
            {/* Lens Border */}
            <div className="absolute inset-0 rounded-full border-2 border-emerald-200/50" />

            {/* Lens Glare */}
            <div className="absolute inset-0">
              <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-white/60 to-transparent rounded-full transform -rotate-45" />
              <div className="absolute bottom-1/4 right-1/4 w-1/3 h-1/3 bg-gradient-to-br from-white/40 to-transparent rounded-full transform rotate-45" />
            </div>

            {/* Centered Leaf Icon */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.05, 0.95, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            >
              <Leaf className="w-20 h-20 text-emerald-600" />
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator - Updated positioning */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-center items-center w-full pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-sm text-gray-600">Scroll to explore</span>
            <div className="w-1 h-8 bg-emerald-200 rounded-full relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-emerald-600 animate-scroll-down" />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
